import { validateRedirectUrl } from '@logto/core-kit';
import { z } from 'zod';
export const oidcModelInstancePayloadGuard = z
    .object({
    userCode: z.string().optional(),
    uid: z.string().optional(),
    grantId: z.string().optional(),
})
    /**
     * Try to use `.passthrough()` if type has been fixed.
     * https://github.com/colinhacks/zod/issues/452
     */
    .catchall(z.unknown());
export const oidcClientMetadataGuard = z.object({
    redirectUris: z
        .string()
        .refine((url) => validateRedirectUrl(url, 'web') || validateRedirectUrl(url, 'mobile'))
        .array(),
    postLogoutRedirectUris: z
        .string()
        .refine((url) => validateRedirectUrl(url, 'web') || validateRedirectUrl(url, 'mobile'))
        .array(),
    backchannelLogoutUri: z.string().url().optional(),
    backchannelLogoutSessionRequired: z.boolean().optional(),
    logoUri: z.string().optional(),
});
export var CustomClientMetadataKey;
(function (CustomClientMetadataKey) {
    CustomClientMetadataKey["CorsAllowedOrigins"] = "corsAllowedOrigins";
    CustomClientMetadataKey["IdTokenTtl"] = "idTokenTtl";
    /** @deprecated Use {@link RefreshTokenTtlInDays} instead. */
    CustomClientMetadataKey["RefreshTokenTtl"] = "refreshTokenTtl";
    CustomClientMetadataKey["RefreshTokenTtlInDays"] = "refreshTokenTtlInDays";
    CustomClientMetadataKey["TenantId"] = "tenantId";
    /**
     * Enabling this configuration will allow Logto to always issue Refresh Tokens, regardless of whether `prompt=consent` is presented in the authentication request.
     *
     * It only works for web applications when the client allowed grant types includes `refresh_token`.
     *
     * This config is for the third-party integrations that do not strictly follow OpenID Connect standards due to some reasons (e.g. they only know OAuth, but requires a Refresh Token to be returned anyway).
     */
    CustomClientMetadataKey["AlwaysIssueRefreshToken"] = "alwaysIssueRefreshToken";
    /**
     * When enabled (default), Logto will issue a new Refresh Token for token requests when 70% of the original Time to Live (TTL) has passed.
     *
     * It can be turned off for only traditional web apps for enhanced security.
     */
    CustomClientMetadataKey["RotateRefreshToken"] = "rotateRefreshToken";
    /**
     * Whether the application is allowed to initiate token exchange requests.
     *
     * Only first-party applications can use token exchange. Third-party applications are always
     * forbidden.
     *
     * Defaults to `false` for all new applications. Users must explicitly enable it.
     */
    CustomClientMetadataKey["AllowTokenExchange"] = "allowTokenExchange";
    /**
     * Whether the application uses the OAuth 2.0 Device Authorization Grant (RFC 8628)
     * instead of the standard Authorization Code flow.
     *
     * Only applicable to native applications. Defaults to `false`.
     */
    CustomClientMetadataKey["IsDeviceFlow"] = "isDeviceFlow";
    /**
     * The maximum number of active sessions (devices) allowed per user for this application.
     *
     * When exceeded, old sessions should be revoked according to server policy.
     */
    CustomClientMetadataKey["MaxAllowedGrants"] = "maxAllowedGrants";
    /**
     * Overrides Logto's hardcoded `client_secret_basic` token-endpoint authentication method
     * (`packages/core/src/oidc/utils.ts`'s `getConstantClientMetadata`) for this Application.
     * `transpileClient` in `packages/core/src/oidc/adapter.ts` spreads `customClientMetadata`
     * after `getConstantClientMetadata`, so a value declared here already wins with no change to
     * either of those files — see `SAI-M7` `design.md` Decision 1's self-review finding.
     *
     * Three values as of `ga-testing-fix-admin-console-auth` (2026-07-22):
     * - `private_key_jwt` — SentryAgent M7 `enterprise-client-authentication`
     * - `client_secret_basic` — Logto's own pre-existing default for confidential clients
     * - `none` — Logto's built-in SPA/Native public clients compute this dynamically via
     *   `getConstantClientMetadata()` at runtime. The guard must include `'none'` because the
     *   `extraClientMetadata.validator` in `packages/core/src/oidc/init.ts` validates ALL
     *   properties in the final merged client metadata through this guard, including
     *   dynamically-computed values — if `'none'` is excluded, Logto's own admin-console SPA
     *   client is rejected with `invalid_client_metadata` on every login attempt.
     *
     *   Including `'none'` means any caller able to PATCH an Application via the Management API
     *   can set `token_endpoint_auth_method: 'none'`, disabling client authentication for that
     *   Application. This risk is accepted for this fix because:
     *   1. Logto's own built-in SPA/Native clients require `'none'` and the guard would
     *      otherwise deny them access entirely (a P0 GA testing finding).
     *   2. Upstream Logto had no guard at all for this field — the M7 patch was an improvement
     *      that happened to be accidentally over-constrained.
     *   3. Proper route-level Management API filtering for `token_endpoint_auth_method` abuse
     *      is a separate, lower-priority improvement.
     *
     * `client_secret_post` and `client_secret_jwt` remain excluded for the same reasons as
     * the original M7 narrowing.
     *
     * @remarks SentryAgent-specific addition (M7 `tenant-isolation-enterprise-tier`). Not present
     * in upstream `logto-io/logto`; tracked as `docker/logto-fork/patches/
     * 0001-private-key-jwt-custom-client-metadata.patch` in the `sentryagent-logto` repo.
     * Widened from 2 to 3 values in `ga-testing-fix-admin-console-auth` (2026-07-22).
     */
    CustomClientMetadataKey["TokenEndpointAuthMethod"] = "token_endpoint_auth_method";
    /**
     * The Application's registered JSON Web Key Set (RFC 7517), used by oidc-provider to verify
     * `private_key_jwt` client-assertion signatures at the token endpoint
     * (`node-oidc-provider`'s `lib/models/client.js` `validateJWKS`) with zero Logto-side
     * involvement — same override mechanism as {@link TokenEndpointAuthMethod} above.
     *
     * Public keys only. This guard's own `.refine()` (see {@link jwkGuard}) rejects any JWK
     * containing private-key members (`d`, `p`, `q`, `dp`, `dq`, `qi`, `k`) as a fail-closed,
     * defense-in-depth backstop — added because the generic Management API Application
     * create/patch routes accept `customClientMetadata.jwks` with no additional filtering today,
     * so until task 3.1 lands there would otherwise be nothing rejecting private-key material
     * PATCHed into this field, where it would be persisted to the DB and readable back via
     * Management API/exports/logs. This schema-level check is **not** a replacement for task 3.1
     * (SAI-M7 `enterprise-client-authentication` tasks.md 3.1): the Management API route is still
     * expected to do its own explicit validation and user-facing error messaging at registration
     * time; this is just the backstop if it doesn't.
     *
     * @remarks SentryAgent-specific addition (M7 `tenant-isolation-enterprise-tier`). Not present
     * in upstream `logto-io/logto`.
     */
    CustomClientMetadataKey["Jwks"] = "jwks";
})(CustomClientMetadataKey || (CustomClientMetadataKey = {}));
/**
 * A single JSON-primitive-or-array-of-primitives value, matching the actual shape of RFC 7517
 * JWK members (`kty`, `n`, `e`, `x`, `y`, `crv`, `kid`, `alg`, `use` are strings; `key_ops`,
 * `x5c` are string arrays). Deliberately not `z.unknown()`: that was the first attempt (SAI-M7
 * spike, 2026-07-18) and it broke TypeScript inference in
 * `packages/cli/src/queries/logto-config.ts`, where a `z.infer<...>` value is passed to
 * `@silverhand/slonik`'s `sql.jsonb()` — that function requires its argument to be assignable to
 * slonik's `SerializableValue`, and `unknown` isn't, while this union is.
 */
const jsonPrimitiveGuard = z.union([z.string(), z.number(), z.boolean()]);
/**
 * RFC 7517/7518 members that only ever appear on private-key material: `d` (RSA/EC private
 * exponent), `p`/`q`/`dp`/`dq`/`qi` (RSA CRT parameters), and `k` (oct symmetric key value). A
 * JWK containing any of these is not a public key.
 */
const privateKeyJwkMembers = ['d', 'p', 'q', 'dp', 'dq', 'qi', 'k'];
/**
 * A single JSON Web Key. See {@link jsonPrimitiveGuard} for why this isn't `z.unknown()`.
 *
 * Rejects any object containing a private-key member ({@link privateKeyJwkMembers}) as a
 * fail-closed, defense-in-depth backstop — see {@link CustomClientMetadataKey.Jwks}'s JSDoc for
 * why this exists at the schema level in addition to the Management API route's own validation
 * (tasks.md 3.1).
 */
const jwkGuard = z
    .record(z.string(), z.union([jsonPrimitiveGuard, z.array(jsonPrimitiveGuard)]))
    .refine((jwk) => !privateKeyJwkMembers.some((member) => member in jwk), {
    message: 'JWK must not contain private-key material',
});
/** A JSON Web Key Set (RFC 7517 §5) — the shape stored under {@link CustomClientMetadataKey.Jwks}. */
const jwksGuard = z.object({
    keys: z.array(jwkGuard),
});
/**
 * Three-value enum: `private_key_jwt`, `client_secret_basic`, and `none`.
 * See {@link CustomClientMetadataKey.TokenEndpointAuthMethod}'s JSDoc for full rationale
 * on each value and why the remaining oidc-provider-native values are excluded.
 */
const tokenEndpointAuthMethodGuard = z.enum(['private_key_jwt', 'client_secret_basic', 'none']);
export const customClientMetadataGuard = z.object({
    [CustomClientMetadataKey.CorsAllowedOrigins]: z.string().min(1).array().optional(),
    [CustomClientMetadataKey.IdTokenTtl]: z.number().optional(),
    [CustomClientMetadataKey.RefreshTokenTtl]: z.number().optional(),
    [CustomClientMetadataKey.RefreshTokenTtlInDays]: z.number().int().min(1).max(180).optional(),
    [CustomClientMetadataKey.TenantId]: z.string().optional(),
    [CustomClientMetadataKey.AlwaysIssueRefreshToken]: z.boolean().optional(),
    [CustomClientMetadataKey.RotateRefreshToken]: z.boolean().optional(),
    [CustomClientMetadataKey.AllowTokenExchange]: z.boolean().optional(),
    [CustomClientMetadataKey.IsDeviceFlow]: z.boolean().optional(),
    [CustomClientMetadataKey.MaxAllowedGrants]: z.number().int().positive().optional(),
    [CustomClientMetadataKey.TokenEndpointAuthMethod]: tokenEndpointAuthMethodGuard.optional(),
    [CustomClientMetadataKey.Jwks]: jwksGuard.optional(),
});
export const oidcSessionAuthorizationDetailsGuard = z
    .object({
    /**
     * The `sid` (session ID) Claim associated with the session for the current client.
     *
     * @remarks
     * Mark optional to make the guard more robust.
     * Should always be present in the session authorization details
     */
    sid: z.string().optional(),
    /**
     * The grantId associated with the session for the current client.
     *
     * @remarks
     * Mark optional to make the guard more robust.
     * Should always be present in the session authorization details when the session is authorized with a grant.
     */
    grantId: z.string().optional(),
    /**
     * Whether the grant associated with the session should be persisted after the session is terminated.
     *
     * @remarks
     * Mark optional to make the guard more robust.
     */
    persistsLogout: z.boolean().optional(),
})
    .catchall(z.unknown());
export const oidcSessionInstancePayloadGuard = z
    .object({
    exp: z.number(),
    iat: z.number(),
    jti: z.string(),
    uid: z.string(),
    kind: z.literal('Session'),
    loginTs: z.number(),
    accountId: z.string(),
    /**
     * A map of client_id to session authorization details. @see OidcSessionAuthorizationDetails
     */
    authorizations: z.record(z.string(), oidcSessionAuthorizationDetailsGuard).optional(),
})
    .catchall(z.unknown());
